import React, { useState } from 'react';
import { Check, X, Zap, Users, Infinity, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SignIn from './SignIn';

interface PricingTier {
  id: string;
  name: string;
  price: number;
  billing: string;
  description: string;
  icon: React.ReactNode;
  features: { text: string; included: boolean }[];
  cta: string;
  popular: boolean;
  minutesIncluded: number;
  doublesLimit: number | null;
  overageRate: number;
}

const pricingTiers: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 15,
    billing: '/month',
    description: 'Perfect for trying DoubleMe',
    icon: <Zap className="h-8 w-8" />,
    minutesIncluded: 60,
    doublesLimit: 1,
    overageRate: 1.75,
    features: [
      { text: '1 AI Double', included: true },
      { text: '60 minutes/month included', included: true },
      { text: 'Basic knowledge base', included: true },
      { text: 'Email support', included: true },
      { text: 'Unlimited Doubles', included: false },
      { text: '5,000+ minutes/month', included: false },
      { text: 'Advanced analytics', included: false },
      { text: 'Priority support', included: false },
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 149,
    billing: '/month',
    description: 'For growing businesses',
    icon: <Users className="h-8 w-8" />,
    minutesIncluded: 1000,
    doublesLimit: 5,
    overageRate: 1.25,
    features: [
      { text: 'Up to 5 AI Doubles', included: true },
      { text: '1,000 minutes/month included', included: true },
      { text: 'Advanced knowledge base', included: true },
      { text: 'Priority email support', included: true },
      { text: 'Unlimited Doubles', included: false },
      { text: '5,000+ minutes/month', included: false },
      { text: 'Advanced analytics', included: false },
      { text: 'Dedicated support', included: false },
    ],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    id: 'business',
    name: 'Business',
    price: 799,
    billing: '/month',
    description: 'For established teams',
    icon: <Infinity className="h-8 w-8" />,
    minutesIncluded: 5000,
    doublesLimit: null,
    overageRate: 0.50,
    features: [
      { text: 'Unlimited AI Doubles', included: true },
      { text: '5,000 minutes/month included', included: true },
      { text: 'Custom knowledge bases', included: true },
      { text: 'Dedicated support', included: true },
      { text: 'Advanced analytics', included: true },
      { text: 'API access', included: true },
      { text: 'Team management', included: true },
      { text: 'SLA guarantee', included: false },
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 2999,
    billing: '/month',
    description: 'For enterprises & scale',
    icon: <Crown className="h-8 w-8" />,
    minutesIncluded: 25000,
    doublesLimit: null,
    overageRate: 0.20,
    features: [
      { text: 'Unlimited AI Doubles', included: true },
      { text: '25,000 minutes/month included', included: true },
      { text: 'Custom integration & setup', included: true },
      { text: '24/7 priority support', included: true },
      { text: 'Advanced analytics & reporting', included: true },
      { text: 'Full API access', included: true },
      { text: 'Multi-team management', included: true },
      { text: '99.9% SLA guarantee', included: true },
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const slideUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

interface PricingCardProps {
  tier: PricingTier;
  onSelectTier: (tierId: string) => void;
}

const PricingCard: React.FC<PricingCardProps> = ({ tier, onSelectTier }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-50px" }}
    variants={slideUp}
    className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${
      tier.popular
        ? 'ring-2 ring-primary shadow-xl scale-105'
        : 'shadow-lg'
    }`}
  >
    {/* Background */}
    <div className={`absolute inset-0 ${
      tier.popular
        ? 'bg-gradient-to-br from-primary/5 via-white to-white'
        : 'bg-white'
    }`} />

    {/* Popular Badge */}
    {tier.popular && (
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10"
      >
        <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
          Most Popular
        </span>
      </motion.div>
    )}

    {/* Content */}
    <div className="relative p-8 flex flex-col h-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className={`p-3 rounded-lg ${
            tier.popular ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-gray-600'
          }`}>
            {tier.icon}
          </div>
          <div>
            <h3 className="text-2xl font-bold text-dark">{tier.name}</h3>
            <p className="text-sm text-gray-600">{tier.description}</p>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-5xl font-bold text-dark">${tier.price}</span>
          <span className="text-gray-600">{tier.billing}</span>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Includes {tier.minutesIncluded.toLocaleString()} minutes/month
        </p>
      </div>

      {/* CTA Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onSelectTier(tier.id)}
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 mb-6 ${
          tier.popular
            ? 'bg-primary text-white hover:bg-primary/90 shadow-lg'
            : 'bg-gray-100 text-dark hover:bg-gray-200'
        }`}
      >
        {tier.cta}
      </motion.button>

      {/* Usage Info */}
      <div className="grid grid-cols-2 gap-3 mb-6 pb-6 border-b border-gray-200">
        <div>
          <p className="text-xs text-gray-600">Included Minutes</p>
          <p className="text-lg font-semibold text-dark">{tier.minutesIncluded.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Doubles Limit</p>
          <p className="text-lg font-semibold text-dark">
            {tier.doublesLimit === null ? 'Unlimited' : tier.doublesLimit}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Overage Rate</p>
          <p className="text-lg font-semibold text-dark">${tier.overageRate}/min</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Trial Period</p>
          <p className="text-lg font-semibold text-dark">14 days</p>
        </div>
      </div>

      {/* Features List */}
      <div className="space-y-3 flex-grow">
        {tier.features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            viewport={{ once: true }}
            className="flex items-start gap-3"
          >
            {feature.included ? (
              <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            ) : (
              <X className="h-5 w-5 text-gray-300 flex-shrink-0 mt-0.5" />
            )}
            <span className={`text-sm ${
              feature.included ? 'text-gray-700' : 'text-gray-400'
            }`}>
              {feature.text}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  </motion.div>
);

const Pricing: React.FC = () => {
  const [showSignIn, setShowSignIn] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  const handleSelectTier = (tierId: string) => {
    setSelectedTier(tierId);
    setShowSignIn(true);
  };

  if (showSignIn) {
    return (
      <SignIn
        onClose={() => setShowSignIn(false)}
        selectedTier={selectedTier}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="pt-32 pb-16 px-4"
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            variants={slideUp}
            className="text-5xl md:text-6xl font-bold text-dark mb-6"
          >
            Simple, Transparent Pricing
          </motion.h1>
          <motion.p
            variants={slideUp}
            className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto"
          >
            No hidden fees. All features available in every tier. Choose the plan that fits your needs.
          </motion.p>

          <motion.div
            variants={slideUp}
            className="flex items-center justify-center gap-4"
          >
            <span className="text-gray-600">14-day free trial included with all plans</span>
            <span className="text-primary font-semibold">No credit card required</span>
          </motion.div>
        </div>
      </motion.section>

      {/* Pricing Cards */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="py-16 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingTiers.map((tier) => (
              <PricingCard
                key={tier.id}
                tier={tier}
                onSelectTier={handleSelectTier}
              />
            ))}
          </div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-20 px-4 bg-gray-50/50"
      >
        <div className="max-w-3xl mx-auto">
          <motion.h2
            variants={slideUp}
            className="text-4xl font-bold text-dark text-center mb-12"
          >
            Frequently Asked Questions
          </motion.h2>

          <motion.div
            variants={stagger}
            className="space-y-6"
          >
            {[
              {
                question: 'Can I change my plan anytime?',
                answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately with automatic billing adjustments.'
              },
              {
                question: 'What happens if I exceed my minutes?',
                answer: 'You\'ll be charged at the overage rate for that tier ($0.20 - $1.75 per minute). Many customers upgrade instead, as it\'s more cost-effective.'
              },
              {
                question: 'Do you offer annual billing discounts?',
                answer: 'Currently we offer monthly billing. Contact our sales team to discuss custom enterprise arrangements.'
              },
              {
                question: 'Can I cancel anytime?',
                answer: 'Absolutely. You can cancel your subscription at any time. You\'ll have access until the end of your billing period.'
              },
              {
                question: 'What\'s included in the free trial?',
                answer: 'The free trial includes full access to your selected tier\'s features and minute allocation for 14 days, with no credit card required.'
              },
              {
                question: 'Do you offer refunds?',
                answer: 'We stand behind our product. Contact our support team if you\'re not satisfied, and we\'ll work with you.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                variants={slideUp}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary transition-colors"
              >
                <h3 className="text-lg font-semibold text-dark mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-16 px-4"
      >
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-2xl p-12">
          <motion.h2
            variants={slideUp}
            className="text-3xl font-bold text-dark mb-4"
          >
            Ready to get started?
          </motion.h2>
          <motion.p
            variants={slideUp}
            className="text-lg text-gray-600 mb-8"
          >
            Start your 14-day free trial today. No credit card required.
          </motion.p>
          <motion.button
            variants={slideUp}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSelectTier('professional')}
            className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Start Free Trial
          </motion.button>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
};

export default Pricing;
